/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import toast from "react-hot-toast";
import { getAllPrinterSlip, getPrinterByIdApi } from "@/api/settings/printer-settings";

const extractSlipId = (data, availableSlips = []) => {
  if (!data) return "";

  const possibleId =
    data.printer_slip_id ??
    (typeof data.printer_slip === "object" ? data.printer_slip?.id : data.printer_slip) ??
    (typeof data.slip === "object" ? data.slip?.id : data.slip) ??
    data.slip_id ??
    (typeof data.printer_slip_format === "object" ? data.printer_slip_format?.id : data.printer_slip_format) ??
    data.printer_slip_format_id ??
    (typeof data.slip_format === "object" ? data.slip_format?.id : data.slip_format) ??
    data.slip_format_id ??
    data.printerSlipId ??
    (typeof data.printerSlip === "object" ? data.printerSlip?.id : data.printerSlip);

  if (possibleId !== null && possibleId !== undefined && possibleId !== "" && !isNaN(Number(possibleId))) {
    return String(possibleId);
  }

  const possibleName =
    (typeof data.printer_slip === "object" ? data.printer_slip?.name : null) ||
    (typeof data.printer_slip === "string" ? data.printer_slip : null) ||
    (typeof data.slip === "object" ? data.slip?.name : null) ||
    (typeof data.slip === "string" ? data.slip : null) ||
    data.slip_name ||
    data.slipFormat ||
    data.slip_format;

  if (possibleName && Array.isArray(availableSlips) && availableSlips.length > 0) {
    const matched = availableSlips.find(
      (s) =>
        s.name?.toLowerCase() === String(possibleName).toLowerCase() ||
        String(s.id) === String(possibleName)
    );
    if (matched) return String(matched.id);
  }

  return "";
};

const menuCategories = [
  { id: 1, name: "Drinks" },
  { id: 2, name: "Standard Foods" },
  { id: 3, name: "Dessert" },
  { id: 4, name: "Tea & Coffee" },
  { id: 5, name: "Pizza" },
  { id: 6, name: "Burger" },
  { id: 7, name: "Kebab" },
  { id: 8, name: "Fish & Chips" },
  { id: 9, name: "Combo Meal" },
  { id: 12, name: "Course" },
  { id: 14, name: "Dish of The Day" },
  { id: 15, name: "Sunday Roast" },
  { id: 16, name: "Banquet Night" },
  { id: 17, name: "Buffet" },
  { id: 18, name: "Christmas Lunch/Table" },
  { id: 19, name: "Party/Occasion platter sample" },
  { id: 20, name: "Mix & Match" },
  { id: 21, name: "Choose Your Own" },
  { id: 22, name: "Create Your Own Pizza" },
];



export function PrinterDialog({ isOpen, onClose, onSubmit, editData, isLoading, restaurantId }) {
  const [slipFormats, setSlipFormats] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    ip:"",
    port:"",
    menu_items: [],
    printer_type: "",
    printer_slip_id: "",
    status: "active",
    is_default: false,
    is_default_till: false,
    default_till: false,
  });

  useEffect(() => {
    getAllPrinterSlip().then((data) => {
      if (data?.status === "success") {
        setSlipFormats(data.data?.printer_slips || []);
      }
    });
  }, []);

  useEffect(() => {
    if (editData) {
      // Extract just IDs from menu_items in case they come as objects
      const menuItemIds = Array.isArray(editData.menu_items)
        ? editData.menu_items.map((item) => (typeof item === "object" ? Number(item.id) : Number(item)))
        : [];
      
      const isDefault = Boolean(
        editData.is_default ||
        editData.is_default_till ||
        editData.default_till ||
        editData.is_default_printer
      );

      const slipId = extractSlipId(editData, slipFormats);

      setFormData({
        name: editData.name || "",
        ip: editData.ip || "",
        port: editData.port || "",
        menu_items: menuItemIds,
        printer_type: editData.printer_type || "",
        printer_slip_id: slipId,
        status: editData.status || "active",
        is_default: isDefault,
        is_default_till: isDefault,
        default_till: isDefault,
      });
    } else {
      setFormData({
        name: "",
        ip: "",
        port: "",
        menu_items: [],
        printer_type: "",
        printer_slip_id: "",
        status: "active",
        is_default: false,
        is_default_till: false,
        default_till: false,
      });
    }
  }, [editData, isOpen, slipFormats]);

  useEffect(() => {
    if (!isOpen || !editData?.id) return;

    getPrinterByIdApi(editData.id).then((res) => {
      if (res?.status === "success") {
        const printer = res.data?.printer || res.data?.printer_setting || res.data;
        if (printer) {
          const resolvedSlipId = extractSlipId(printer, slipFormats);
          if (resolvedSlipId) {
            setFormData((prev) => ({
              ...prev,
              printer_slip_id: resolvedSlipId,
            }));
          }
        }
      }
    });
  }, [isOpen, editData?.id, slipFormats]);

  const handleCheckboxChange = (categoryId) => {
    const numId = Number(categoryId);
    setFormData((prev) => ({
      ...prev,
      menu_items: prev.menu_items.some((id) => Number(id) === numId)
        ? prev.menu_items.filter((id) => Number(id) !== numId)
        : [...prev.menu_items, numId],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const submitData = {
      ...formData,
      restaurant_id: restaurantId,
    };
    onSubmit(submitData);
  };

  const handleClose = () => {
    setFormData({
      name: "",
      ip: "",
      port: "",
      menu_items: [],
      printer_type: "",
      printer_slip_id: "",
      status: "active",
      is_default: false,
      is_default_till: false,
      default_till: false,
    });
    onClose();
  };

  // Reset form after closing
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        name: "",
        ip: "",
        port: "",
        menu_items: [],
        printer_slip_id: "",
        printer_type: "",
        status: "active",
        is_default: false,
        is_default_till: false,
        default_till: false,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    getAllPrinterSlip().then((data) => {
      if (data?.status === "success") {
        const slips = data.data?.printer_slips || [];
        setSlipFormats(slips);

        if (editData) {
          const resolvedSlipId = extractSlipId(editData, slips);
          if (resolvedSlipId) {
            setFormData((prev) => ({
              ...prev,
              printer_slip_id: prev.printer_slip_id || resolvedSlipId,
            }));
          }
        }
      } else {
        toast.error(data.message || "Failed to fetch printer slips.");
      }
    });
  }, [isOpen, editData]);

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[650px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl">
            {editData ? "Edit Printer" : "Add New Printer"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name Field */}
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              placeholder="Enter name"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              required
            />
          </div>

          {/* IP Address */}
          <div className="space-y-2">
            <Label htmlFor="ip">IP Address</Label>
            <Input
              id="ip"
              placeholder="Enter IP address"
              value={formData.ip}
              onChange={(e) =>
                setFormData({ ...formData, ip: e.target.value })
              }
              required
            />
          </div>

          {/* Port */}
          <div className="space-y-2">
            <Label htmlFor="port">Port</Label>
            <Input
              id="port"
              placeholder="Enter port number"
              value={formData.port}
              onChange={(e) =>
                setFormData({ ...formData, port: e.target.value })
              }
              required
              type="number"
              min="1"
              max="65535"
            />
          </div>

          {/* Menu Category */}
          <div className="space-y-2">
            <Label className=" text-xl">Menu Category</Label>
            <div className="grid grid-cols-3 gap-3">
              {menuCategories.map((category) => (
                <div key={category.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={category.id}
                    checked={formData.menu_items.some((id) => Number(id) === Number(category.id))}
                    onCheckedChange={() => handleCheckboxChange(category.id)}
                  />
                  <label
                    htmlFor={category.id}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                  >
                    {category.name}
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* Printer Type */}
          <div className="space-y-2">
            <Label htmlFor="printer_type">Printer Type</Label>
            <Select
              value={formData.printer_type}
              onValueChange={(value) =>
                setFormData({ ...formData, printer_type: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select Printer Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="kitchen">Kitchen</SelectItem>
                <SelectItem value="till">Till</SelectItem>
                <SelectItem value="bar">Bar</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Printing Slip Format */}
          <div className="space-y-2">
            <Label htmlFor="printer_slip_id">Printing Slip Format</Label>
            <Select
              value={formData.printer_slip_id ? String(formData.printer_slip_id) : undefined}
              onValueChange={(value) =>
                setFormData({ ...formData, printer_slip_id: value })
              }
              required
            >
              <SelectTrigger>
                <SelectValue placeholder="Select slip Format" />
              </SelectTrigger>
              <SelectContent>
                {slipFormats.map((format) => (
                  <SelectItem key={format.id} value={String(format.id)}>
                    {format.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) =>
                setFormData({ ...formData, status: value })
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Default Till Printer Checkbox - Only for Till Printer Type */}
          {formData.printer_type?.toLowerCase() === "till" && (
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="is_default_till_printer"
                checked={formData.is_default}
                onCheckedChange={(checked) =>
                  setFormData({
                    ...formData,
                    is_default: !!checked,
                    is_default_till: !!checked,
                    default_till: !!checked,
                  })
                }
              />
              <label
                htmlFor="is_default_till_printer"
                className="text-sm font-medium leading-none cursor-pointer"
              >
                Make as Default Till Printer
              </label>
            </div>
          )}

          {/* Submit Button */}
          <div className="pt-4">
            <Button
              type="submit"
              className="w-full bg-green-600 hover:bg-green-700"
              isLoading={isLoading}
            >
              Confirm
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
